updateInvoices<template>
  <fullscreen
    background="#F3F5F7"
    class="table-fullscreen table__theme-default"
    ref="fullscreen"
    @change="fullScreenChange"
  >
    <!-- confirm doesnt open if it put inside fullscreen -->
    <ConfirmModal ref="confirm" />
    <TableRowModal
      v-model="tableRowModal.show"
      :title="tableRowModal.title"
      :items="tableRowModal.items"
      :tableRowModalFields="tableRowModalFields"
    />
    <ModalTableDescription
      v-model="tableDescriptionModal.show"
      :description="tableDescription"
    />

    <div class="table-controls" v-if="showTopControls">
      <div class="table-controls-left">
        <slot name="tableControls"></slot>
      </div>
      <div class="table-controls-right">
        <div class="table-controls-right__item">
          <slot name="tableControlsRight"></slot>
        </div>
        <div class="table-controls-right__item">
          <a
            class="link"
            v-if="tableDescription.length"
            @click.prevent="openModalTableDescription"
          >
            Description
          </a>
        </div>
        <b-button
          class="button btn-icon"
          variant="outline-info"
          v-if="isFullScreen"
          @click="toggleFullscreen"
        >
            <span class="material-icons">
              fullscreen_exit
            </span>
        </b-button>
      </div>
    </div>
    <div class="d-flex justify-content-between">
      <div>
        <b-pagination
          class="mt-0"
          size="md"
          v-if="isShowPagination"
          :total-rows="getTotalTableData"
          :current-page="table.options.currentPage"
          :value="table.options.currentPage"
          :per-page="table.options.perPage"
          @change="paginationClickedEvent"
        />
      </div>

      <div class="d-flex">
        <slot name="filtersSelect"></slot>
      </div>
    </div>
    <div
      class="table-container"
      :class="{
        'b-table-sticky-header': stickyHeader,
        'h-100': fullHeight,
        'overflow-hidden': hidePartTable
      }"
      :style="{ height: hidePartTable }"
      ref="tableContain"
    >
        <b-table
          :primary-key="primaryKeyForTable"
          :fields="fields"
          :items="getCurrentTableData"
          :current-page="isCurrentFirstPage ? getCurrentPage : 1"
          :per-page="table.options.perPage"
          :sort-by="table.options.sortBy"
          :sort-desc="table.options.sortDesc"
          :no-local-sorting="noLocalSorting"
          :thead-tr-class="['main-head', extraTheadClass]"
          @head-clicked="headClicked"
          @row-clicked="rowClicked"
          @row-hovered="rowHovered"
          @row-dblclicked="rowDblClicked"
          :fixed="cellFixed"
          hover
          :sticky-header="stickyHeader ? '500px' : false"
          style="margin: 0;"
          show-empty
        >
          <template #empty>
            <div class="d-flex justify-content-center p-5">
              <h4>Table is empty</h4>
            </div>
          </template>
          <template #thead-top="data" v-if="stickyTableHead">
            <b-tr class="thead-row" :class="{'sticky-head': getCoordinates > 0}" :style="{'top': getCoordinates + 'px'}" colspan="1">
              <template v-for="(item, i) in data.fields">
                <b-th :key="item.key" :style="{'width': calcWidth(extraTheadClass ? extraTheadClass + i : i) }" :aria-colindex="i +1">
                  <div>{{ item.label }}</div>
                </b-th>
              </template>
            </b-tr>
          </template>
          <template
            v-slot:[getSlotName(slot)]="data"
            v-for="slot in slots"
          >
            <div class="" :key="slot.type ? `${slot.type}(${slot.name})` : slot.name">
              <div>
                <slot
                  :name="(slot.type ? `${slot.type}(${slot.name})` : slot.name)"
                  v-bind="data"
                />
              </div>
              <TextEditor
                v-if="isComment && slot.comment"
                class="position-absolute editor-block"
                :in-modal="modalTextEditor"
                :entity-type="slot.comment.entity_type"
                :entity-id="data.item[slot.comment.entity_id_field]"
                :data-already-loaded="slot.comment.data_already_loaded"
              />
            </div>
          </template>
          <template v-slot:cell(actions)="data">
            <div class="btn-group btn-group__actions" role="group">
              <b-dropdown
                class="dropdown__theme-light navbar__dropdown table__dropdown"
                right
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <span class="material-icons icon-image-preview">
                    more_vert
                  </span>
                </template>
                <b-dropdown-item-button
                  v-if="isShowView"
                  @click.prevent="showView(data)"
                >
                <span class="dropdown-item-link">
                  View
                </span>
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  v-for="action in actions"
                  v-if="typeof action.show === 'function' ? action.show(data.item) : action.show"
                  @click.prevent="$emit(action.event, data.item)"
                >
                <span class="dropdown-item-link">
                  {{ action.label }}
                </span>
                </b-dropdown-item-button>
              </b-dropdown>
            </div>
          </template>
          <template v-slot:cell(more)="data">
            <b-button
              size="sm"
              class="button btn-icon"
              variant="secondary"
              @click="data.toggleDetails"
              v-if="isShowMoreButton"
            >
              <slot name="buttonShow" :buttonShow="data">
                <span class="material-icons">
                  {{ data.detailsShowing ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                </span>
              </slot>
            </b-button>
          </template>
          <template v-slot:row-details="data">
            <slot name="row" :row="data"></slot>
          </template>
        </b-table>
        <slot name="slideOut"></slot>
        <slot name="total" v-if="showTotalTable">
          <div class="table__total t-head-show">
            <div class="table-container">
              <b-table
                class="font-weight-bold"
                thead-class="thead-total"
                :items="totalItems"
                :fields="totalFields"
                hover
                :fixed="cellFixed"
              >
                <template
                  v-slot:[`cell(${slot.name})`]="data"
                  v-for="(slot, slotName) in totalSlots"
                >
                  <slot :name="slotName" v-bind="data"></slot>
                </template>
              </b-table>
            </div>
          </div>
        </slot>
        <div
          v-if="hidePartTable && getCurrentTableData.length > 1"
          class="hide-arrow"
          @click="togglePartTable()"
        >
          <span class="material-icons">
            {{ fullHeight ? 'arrow_drop_up' : 'arrow_drop_down' }}
          </span>
        </div>
    </div>
    <b-pagination
      ref="pagination"
      size="md"
      v-if="isShowPagination"
      :total-rows="getTotalTableData"
      :current-page="table.options.currentPage"
      :value="table.options.currentPage"
      :per-page="table.options.perPage"
      @change="paginationClickedEvent"
      @page-click="clickPagination"
    />
  </fullscreen>

</template>

<script>
import { permissions } from '@mocks/permissions'
import TableRowModal from './TableRowModal'
import ModalTableDescription from '@components/modals/ModalTableDescription'
import ConfirmModal from '@components/modals/ConfirmModal'
import TextEditor from '@components/TextEditor'
import StickyHeaderMixin from '@/mixins/StickyHeaderMixin'

export default {
  name: 'TableForPage',
  components: {
    TableRowModal,
    ModalTableDescription,
    ConfirmModal,
    TextEditor
  },
  mixins: [StickyHeaderMixin],
  data () {
    return {
      permissions: permissions,
      fullscreen: false,
      table: {
        options: {
          currentPage: 1,
          perPage: 15,
          sortBy: null,
          sortDesc: false
        }
      },
      tableRowModal: {
        show: false,
        items: null,
        title: null
      },
      tableDescriptionModal: {
        show: false
      },
      fullHeight: false
    }
  },
  props: {
    primaryKeyForTable: {
      type: String,
      default: 'id'
    },
    totalItems: {
      type: Array
    },
    totalFields: {
      type: Array
    },
    showTotalTable: {
      type: Boolean,
      default: false
    },
    tableDescription: {
      type: Array,
      default: () => []
    },
    showTopControls: {
      type: Boolean,
      default: false
    },
    isServerRender: {
      type: Boolean,
      default: false
    },
    cellFixed: {
      type: Boolean,
      default: false
    },
    stickyHeader: {
      type: Boolean,
      default: false
    },
    slots: {
      type: Object,
      default: () => {}
    },
    totalSlots: {
      type: Object,
      default: () => {}
    },
    tableData: {
      type: [Array, Object],
      default: () => ([])
    },
    isEdit: {
      type: Boolean
    },
    isRemove: {
      type: Boolean
    },
    actions: {
      type: Array,
      default: () => ([])
    },
    isShowView: {
      type: Boolean,
      default: false
    },
    isShowMoreButton: {
      type: Boolean,
      default: true
    },
    isFullScreen: {
      type: Boolean,
      default: true
    },
    fields: {
      type: Array,
      default: () => []
    },
    totalRows: {
      type: Number
    },
    currentPage: {
      type: Number,
      default: 1
    },
    perPage: {
      type: Number,
      default: 15
    },
    sortBy: {
      type: String,
      default: null
    },
    noLocalSorting: {
      type: Boolean,
      default: false
    },
    tableRowModalFields: {
      type: Array
    },
    dataSelect: {
      type: Array,
      default: () => ([])
    },
    isComment: {
      type: Boolean,
      default: false
    },
    modalTextEditor: {
      type: Boolean,
      default: true
    },
    theadTop: {
      type: Boolean,
      default: false
    },
    extraTheadClass: {
      type: String,
      default: ''
    },
    hidePartTable: {
      type: String,
      default: ''
    },
    isCurrentFirstPage: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    getCurrentTableData () {
      if (!Array.isArray(this.tableData)) {
        return Object.values(this.tableData)
      }
      return this.tableData
    },
    getTotalTableData () {
      return this.totalRows || this.tableData.length
    },
    isShowPagination () {
      return this.getTotalTableData > this.perPage
    },
    getCurrentPage () {
      return this.isServerRender ? 1 : this.table.options.currentPage
    }
  },
  watch: {
    'currentPage': function () {
      this.initialParams()
    },
    getTotalTableData () {
      this.initialParams()
    },
    fields (v) {
      this.fields = v
      setTimeout(() => {
        this.getAllTheadWidth(this.extraTheadClass)
      }, 10)
    },
    totalFields (v) {
      this.totalFields = v
    }
  },
  created () {
    this.initialParams()
    this.onUpdateTable()
  },
  mounted () {
    if (this.isEdit) {
      this.actions.push({ label: 'Edit', event: 'edit', show: true})
    }
    if (this.isRemove) {
      this.actions.push({ label: 'Delete', event: 'remove', show: true})
    }
    this.getAllTheadWidth(this.extraTheadClass)
  },
  methods: {
    openModalTableDescription () {
      this.tableDescriptionModal.show = true
    },
    toggleFullscreen () {
      this.$refs['fullscreen'].toggle()
    },
    fullScreenChange (fullscreen) {
      this.fullscreen = fullscreen
    },
    initialParams () {
      this.table.options.currentPage = this.currentPage
      this.table.options.perPage = this.perPage
      this.table.options.sortBy = this.sortBy
    },
    headClicked (key, value) {
      if (!this.noLocalSorting || !value.sortable) return

      this.table.options.sortDesc = this.table.options.sortBy !== key ? false : !this.table.options.sortDesc
      this.table.options.sortBy = key

      this.onUpdateTable()
    },
    rowClicked (item, index, event) {
      this.$emit('row-clicked', { item, index, event })
    },
    rowDblClicked (item, index, event) {
      this.$emit('row-dblclicked', { item, index, event })
    },
    rowHovered (item, index, event) {
      this.$emit('row-hovered', { item, index, event })
    },
    resetPagination () {
      this.paginationClickedEvent(1)
    },
    isInViewport (element) {
      const rect = element.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    },
    clickPagination () {
      this.scrollToPagination()
    },
    scrollToPagination () {
      setTimeout(() => {
        this.isInViewport(this.$refs['pagination'].$el) || this.$refs['pagination'].$el.scrollIntoView({ block: 'end', behavior: 'smooth' })
      }
      , 10)
    },
    paginationClickedEvent (page) {
      this.table.options.currentPage = page
      this.onUpdateTable()
    },
    onUpdateTable () {
      this.$emit('update', this.table.options)
    },
    showView (row, title) {
      this.tableRowModal.show = true
      this.tableRowModal.title = title
      this.tableRowModal.items = row.item
    },
    getRowData (obj, path) {
      return path.split('.').reduce((prev, curr) => {
        return prev ? prev[curr] : null
      }, obj)
    },
    getSlotName (slot) {
      return `${slot.type ?? `cell`}(${slot.name})`
    },
    toggleShowColumns () {
      this.showColumns = !this.showColumns
    },
    togglePartTable () {
      this.fullHeight = !this.fullHeight
    }
  }
}
</script>

<style lang="scss">
  .material-icons {
    &.no-data-icon {
      width: 30px;
      height: 30px;
      font-size: 30px;
    }
  }
  .table-container {
    position: relative;
  }

  .thead-row {
    display: none;
    &.sticky-head {
      height: 75px;
      padding: 15px;
      display: table-row;
      background-color: #fff;
      position: absolute;
      left: 0;
      right: 0;
      z-index: 9;
    }
  }
  .table__total.t-head-show {
    .table.b-table {
      .thead-total {
        display: table-header-group !important;

        th {
          opacity: 0;
        }
      }
      tbody {
        tr {
          border-top: 0;
        }
      }
    }

  }
  .editor-block {
    top: 50%;
    right: 0;
    transform: translate(-50%, -50%)
  }
  .hide-arrow {
    width: 100%;
    height: 18px;
    text-align: center;
    cursor: pointer;
    position: absolute;
    bottom: 0px;
    left: 0;
    z-index: 3;
    box-shadow: 0px -7px 8px -4px rgba(0, 0, 0, 0.2) inset;

    &:hover {
      background: rgba(0, 0, 0, .1);
    }
  }
</style>
